import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import './ImportProducts.scss';
import Menu from '../../components/Menu/Menu';
import MobileMenu from '../../components/Menu/MobileMenu';
import { importService } from "../../services/ImportService";
import AttachementField from "../../components/AttachementField/AttachementField.jsx";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from "../../components/Header/Header.jsx";

const STATUS_FILTERS = ['all', 'success', 'error', 'other'];
const STATUS_LABELS = {
    all: 'Toate',
    success: 'Success',
    error: 'Eroare',
    other: 'Altele'
};

const getItemDisplayName = (item, index) => {
    if (!item || typeof item !== 'object') return `Produs #${index + 1}`;
    return item.product_name || item.name || item.product?.name || item.barcode || `Produs #${index + 1}`;
};

const getItemDisplayMessage = (item) => {
    if (!item || typeof item !== 'object') return '';
    return item.error || item.reason || item.warning || item.message || '';
};

const STREAM_RESULT_BUCKETS = [
    { key: 'created_products', status: 'success', source: 'created' },
    { key: 'updated_products', status: 'success', source: 'updated' },
    { key: 'unchanged_products', status: 'other', source: 'unchanged' },
    { key: 'not_updated_products', status: 'other', source: 'not_updated' },
    { key: 'error_products', status: 'error', source: 'error' },
    { key: 'imported_successfully_products', status: 'success', source: 'imported_successfully' }
];

const mapStatusToCountKey = (status) => {
    const normalizedStatus = (status || '').toString().toLowerCase();
    if (normalizedStatus === 'created') return 'created';
    if (normalizedStatus === 'updated') return 'updated';
    if (normalizedStatus === 'unchanged') return 'unchanged';
    if (normalizedStatus === 'not_updated' || normalizedStatus === 'not-updated') return 'not_updated';
    if (normalizedStatus === 'error' || normalizedStatus === 'failed' || normalizedStatus === 'fail') return 'errors';
    return null;
};

const extractRowsFromPayload = (payload) => {
    const rows = [];
    if (!payload || typeof payload !== 'object') {
        return rows;
    }

    STREAM_RESULT_BUCKETS.forEach(({ key, status, source }) => {
        const items = Array.isArray(payload?.[key]) ? payload[key] : [];
        items.forEach((item, index) => {
            const rowNumber = item?.row_number ?? `idx-${index}`;
            const itemName = getItemDisplayName(item, index);
            rows.push({
                id: `${source}-${rowNumber}-${item?.product_id || item?.barcode || itemName}`,
                status,
                title: itemName,
                message: getItemDisplayMessage(item),
                source,
                rowNumber: item?.row_number,
                raw: item
            });
        });
    });

    return rows;
};

const mergeUniqueRows = (previousRows, newRows) => {
    if (!newRows.length) return previousRows;
    const existingIds = new Set(previousRows.map((item) => item.id));
    const uniqueRows = newRows.filter((item) => !existingIds.has(item.id));
    return uniqueRows.length ? [...previousRows, ...uniqueRows] : previousRows;
};

const buildImportRow = ({ item, status, source, fallbackIndex = 0 }) => {
    const title = getItemDisplayName(item, fallbackIndex);
    return {
        id: `${source}-${item?.row_number || 'row'}-${item?.product_id || item?.barcode || title}`,
        status,
        title,
        message: getItemDisplayMessage(item),
        source,
        rowNumber: item?.row_number,
        raw: item
    };
};

const flattenProductsByStatus = (payload) => {
    return extractRowsFromPayload(payload);
};

const inferStatus = (eventName, payload) => {
    const payloadStatus = payload?.status || payload?.state;
    if (typeof payloadStatus === 'string') {
        const normalizedPayloadStatus = payloadStatus.toLowerCase();
        if (normalizedPayloadStatus.includes('error') || normalizedPayloadStatus.includes('fail')) return 'error';
        if (normalizedPayloadStatus.includes('success') || normalizedPayloadStatus.includes('created') || normalizedPayloadStatus.includes('updated')) return 'success';
    }

    const normalizedEventName = (eventName || '').toLowerCase();
    if (normalizedEventName.includes('error') || normalizedEventName.includes('fail')) return 'error';
    if (normalizedEventName.includes('success') || normalizedEventName.includes('created') || normalizedEventName.includes('updated')) return 'success';

    return 'other';
};

const ImportProducts = () => {
    const importAbortControllerRef = useRef(null);
    const [productsFile, setProductsFile] = useState({ name: '' });
    const [disabledDocument, setDisabledDocument] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [importRows, setImportRows] = useState([]);
    const [importMeta, setImportMeta] = useState({
        totalRows: 0,
        processedRows: 0,
        categoriesProcessed: 0
    });
    const [importCounts, setImportCounts] = useState({
        created: 0,
        updated: 0,
        unchanged: 0,
        not_updated: 0,
        errors: 0
    });
    const hasImportResults = useMemo(() => {
        const countValues = Object.values(importCounts || {});
        const hasAnyCount = countValues.some((value) => Number(value) > 0);
        const hasMetaProgress = Number(importMeta.processedRows) > 0 || Number(importMeta.totalRows) > 0;
        return importRows.length > 0 || hasAnyCount || hasMetaProgress;
    }, [importRows, importCounts, importMeta]);

    const liveCounts = useMemo(() => {
        const counters = {
            created: 0,
            updated: 0,
            unchanged: 0,
            not_updated: 0,
            errors: 0
        };

        importRows.forEach((row) => {
            const key = mapStatusToCountKey(row?.raw?.status || row?.source || row?.status);
            if (key) {
                counters[key] += 1;
            }
        });

        return counters;
    }, [importRows]);

    const displayedCounts = useMemo(() => {
        // Prefer live-calculated counters during streaming so badges move in real time.
        if (isImporting) return liveCounts;

        // After streaming ends, backend summary counts are source of truth if present.
        const hasBackendCounts = Object.values(importCounts || {}).some((value) => Number(value) > 0);
        return hasBackendCounts ? importCounts : liveCounts;
    }, [isImporting, importCounts, liveCounts]);

    const showLivePanel = isImporting || hasImportResults;

    useEffect(() => () => {
        if (importAbortControllerRef.current) {
            importAbortControllerRef.current.abort();
            importAbortControllerRef.current = null;
        }
    }, []);

    const handleFileDrop = (documents) => {
        const file = documents[0];
        if (!isExcelFile(file)) {
            toast.error("Te rog să încarci un fișier Excel.");
            return;
        }

        setProductsFile({
            name: file.name,
            file,
        });
        setDisabledDocument(true);
    };

    const removeDocument = () => {
        setProductsFile({ name: '' });
        setDisabledDocument(false);
    };

    const isExcelFile = (file) => {
        const allowedTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        return allowedTypes.includes(file.type);
    };
    const handleSubmitProducts = () => {
        if (!productsFile.file) {
            toast.warning("Please attach a product file before submitting.", {
                toastId: "no-product-file",
            });
            return;
        }

        setDisableSubmit(true);
        setIsImporting(true);
        setImportRows([]);
        setImportMeta({ totalRows: 0, processedRows: 0, categoriesProcessed: 0 });
        setImportCounts({ created: 0, updated: 0, unchanged: 0, not_updated: 0, errors: 0 });
        if (importAbortControllerRef.current) {
            importAbortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        importAbortControllerRef.current = abortController;

        importService.importProductsWithStream(
            { file: productsFile.file },
            {
                signal: abortController.signal,
                onEvent: ({ event, data }) => {
                    if (typeof data === 'object' && data !== null) {
                        setImportMeta((prev) => ({
                            totalRows: data.total_rows ?? prev.totalRows,
                            processedRows: data.processed_rows ?? prev.processedRows,
                            categoriesProcessed: data.categories_processed ?? prev.categoriesProcessed
                        }));

                        if (data.counts && typeof data.counts === 'object') {
                            setImportCounts((prev) => ({ ...prev, ...data.counts }));
                        }
                    }

                    if (typeof data !== 'object' || data === null) {
                        return;
                    }

                    // Handle progress payloads that already contain partial product arrays.
                    const bucketRows = extractRowsFromPayload(data);
                    if (bucketRows.length > 0) {
                        setImportRows((prev) => mergeUniqueRows(prev, bucketRows));
                        return;
                    }

                    // Handle progress payload format:
                    // { status, processed_rows, ..., row_result: { product_name, row_number, ... } }
                    const rowResult = data?.row_result && typeof data.row_result === 'object' ? data.row_result : null;
                    if (rowResult) {
                        const mergedRowPayload = {
                            ...rowResult,
                            status: rowResult.status || data.status
                        };
                        const rowStatus = inferStatus(event, mergedRowPayload);
                        const progressRow = [
                            buildImportRow({
                                item: mergedRowPayload,
                                status: rowStatus,
                                source: mergedRowPayload.status || data.status || event || 'progress'
                            })
                        ];
                        setImportRows((prev) => mergeUniqueRows(prev, progressRow));
                        return;
                    }

                    // Handle one-product-per-event payloads.
                    const isProductLevelEvent = data.product_name || data.name || data.barcode || data.row_number || data.error || data.reason || data.warning;
                    if (isProductLevelEvent) {
                        const status = inferStatus(event, data);
                        const directRow = [
                            buildImportRow({
                                item: data,
                                status,
                                source: data?.status || event || 'stream'
                            })
                        ];
                        setImportRows((prev) => mergeUniqueRows(prev, directRow));
                    }
                },
                onDone: (payload) => {
                    const rowsFromPayload = flattenProductsByStatus(payload);
                    if (rowsFromPayload.length > 0) {
                        setImportRows(rowsFromPayload);
                    }

                    if (payload?.counts && typeof payload.counts === 'object') {
                        setImportCounts((prev) => ({ ...prev, ...payload.counts }));
                    }

                    setImportMeta((prev) => ({
                        totalRows: payload?.total_rows ?? prev.totalRows,
                        processedRows: payload?.processed_rows ?? prev.processedRows,
                        categoriesProcessed: payload?.categories_processed ?? prev.categoriesProcessed
                    }));
                }
            }
        )
            .then(() => {
                toast.success("Products imported successfully.", { toastId: "product-import-success" });
                setProductsFile({ name: '' });
                setDisabledDocument(false);
            })
            .catch((error) => {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error(error);
                handleApiError(error, "Failed to import products.");
            })
            .finally(() => {
                setDisableSubmit(false);
                setIsImporting(false);
                if (importAbortControllerRef.current === abortController) {
                    importAbortControllerRef.current = null;
                }
            });
    };

    const handleApiError = (error, defaultMsg = "Import failed.") => {
        const data = error?.response?.data;
        if (data) {
            toast.error(data.message || defaultMsg, {
                toastId: "api-main-error",
            });
            if (data.errors && typeof data.errors === 'object') {
                Object.entries(data.errors).forEach(([fileName, errorMessages]) => {
                    errorMessages.forEach(msg => {
                        toast.error(`${fileName}: ${msg}`, {
                            toastId: `${fileName}-${msg}`,
                        });
                    });
                });
            }
        } else {
            toast.error(defaultMsg, {
                toastId: "api-generic-error",
            });
        }
    };

    const filteredRows = useMemo(() => {
        if (statusFilter === 'all') return importRows;
        return importRows.filter((item) => item.status === statusFilter);
    }, [statusFilter, importRows]);

    return (
        <div className="standard import">
            <MobileMenu />
            <Header />

            <div className='standard__container'>

                <Menu />
                <div className='standard__wrapper ' style={{ padding: 24, minHeight: 280 }}>
                    <h2 className='standard__page-title'>Import produse</h2>
                    <h4 className='standard__page-info'>
                        Actualizează sau adaugă produse prin import de fișiere.
                    </h4>
                    <div className='import__wrapper'>
                        <div className="import__container" style={{ padding: 24, minHeight: 280 }}>
                            <div className="import__section">
                                <div className="import__upload">
                                    <AttachementField
                                        attachmentLabel="Importă produse"
                                        hideLabel
                                        onDrop={handleFileDrop}
                                        disabledDocuments={disabledDocument}
                                        document={{ url: productsFile.file?.name }}
                                        removeDocument={removeDocument}
                                        documentFilename={productsFile.file?.name}
                                    />
                                </div>

                                <div className='import__submit-row'>
                                    <Button
                                        type="primary"
                                        size="large"
                                        className='import__submit-button'
                                        onClick={handleSubmitProducts}
                                        disabled={!productsFile.file || disableSubmit}
                                        loading={isImporting}
                                    >
                                        Importă produse
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className='import__right-side'>
                            {showLivePanel ? (
                                <>
                                    <div className='import__live-header'>
                                        <h3>Import live produse</h3>
                                        <span>{isImporting ? 'Se procesează...' : 'Import finalizat'}</span>
                                    </div>

                                    <div className='import__live-meta'>
                                        <div>Total rânduri: <strong>{importMeta.totalRows || 0}</strong></div>
                                        <div>Procesate: <strong>{importMeta.processedRows || 0}</strong></div>
                                        <div>Categorii: <strong>{importMeta.categoriesProcessed || 0}</strong></div>
                                    </div>

                                    <div className='import__live-counts'>
                                        <span>Created: {displayedCounts.created || 0}</span>
                                        <span>Updated: {displayedCounts.updated || 0}</span>
                                        <span>Unchanged: {displayedCounts.unchanged || 0}</span>
                                        <span>Not updated: {displayedCounts.not_updated || 0}</span>
                                        <span>Errors: {displayedCounts.errors || 0}</span>
                                    </div>

                                    <div className='import__status-filters'>
                                        {STATUS_FILTERS.map((statusKey) => (
                                            <button
                                                key={statusKey}
                                                type='button'
                                                className={`import__filter-chip ${statusFilter === statusKey ? 'active' : ''}`}
                                                onClick={() => setStatusFilter(statusKey)}
                                            >
                                                {STATUS_LABELS[statusKey]}
                                            </button>
                                        ))}
                                    </div>

                                    <div className='import__live-list'>
                                        {filteredRows.length === 0 ? (
                                            <div className='import__empty-state'>
                                                Nu există evenimente de import încă.
                                            </div>
                                        ) : (
                                            filteredRows.map((item) => (
                                                <div key={item.id} className={`import__live-row import__live-row--${item.status}`}>
                                                    <div className='import__live-row-main'>
                                                        <div className='import__live-row-title'>{item.title}</div>
                                                        <div className='import__live-row-source'>
                                                            {item.rowNumber ? `Rand ${item.rowNumber}` : 'Rand -'}
                                                            {item.source ? ` • ${item.source}` : ''}
                                                            {item.message ? ` • ${item.message}` : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3>Apasă pe butonul alăturat pentru a încărca fișierul cu produsele dorite.</h3>
                                    <img src='/images/upload-icon.png' alt='upload' />
                                </>
                            )}
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default ImportProducts;
